
import Head from 'next/head'
import Script from 'next/script'
import Link from 'next/link'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { getArticleDateTime } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHealthFitness, getWpNewHealthFitness, setWpHealthFitness, setWpNewHealthFitness } from '../../../service/wp-blogs-store'
import { PersonalTrainerHead } from '@/index/service-category/personal-trainer/PersonalTrainerHead'
import { useEffect, useState } from 'react'
import { openModal } from 'service/modal-service'
import { PersonalTrainerWhyUs } from '@/index/service-category/personal-trainer/PersonalTrainerWhyUs'
import { TrainerCustomerReviews } from '@/index/service-category/personal-trainer/TrainerCustomerReviews'
import { ptFreelanceFaqs } from 'utils/faq'
import { FreelanceTrainerBenefits } from '@/index/service-category/personal-trainer/FreelanceTrainerBenefits'
import { useRouter } from 'next/router'
import { RequestProvider } from 'context/RequestContext'
import { getTestimonials } from 'api/user/profile-api'
import { getPtTestimonials, setPtTestimonials } from 'service/testimonial-store'
import { downloadTopServiceProviders } from 'api/service-provider/sp-list'
import { isFreelanceTrainer } from 'utils/trainer-employment'
import { withDevRosterFallback } from 'utils/dev-roster-fallback'
import { toTrainerCards } from 'utils/trainer-card-projection'
import { buildTrainerItemList } from 'utils/trainer-list-schema'
import { getDistanceMetrics } from 'utils/distance-matrix'
import { DarkFaq } from '@/index/service-category/personal-trainer/DarkFaq'
import { FaCheckCircle } from 'react-icons/fa'
import { TopTrainersSection } from '@/index/service-category/personal-trainer/TopTrainersSection'
import { HERO_TRAINER_COUNT } from '@/index/service-category/personal-trainer/HeroTrainerStrip'
import { FreelanceTrainerComparison } from '@/index/service-category/personal-trainer/FreelanceTrainerComparison'
import TrustedPartners from '@/index/service-category/personal-trainer/TrustedPartners'
import { localBusinessNode, buildServiceNode } from 'utils/local-business-schema'

// Competing directories list ~28 trainers. The ranking puts every employed
// trainer ahead of the first freelancer (33 of them in Oct 2026), so fetch
// deep enough to clear that block and still fill the page.
const ROSTER_FETCH_SIZE = 200
const ROSTER_DISPLAY_LIMIT = 30

export async function getStaticProps() {
    
    const { modifiedDate, month, year } = getArticleDateTime();
    const fitnessBlogs = blogsMaster['health-fitness'].blogArray;
    const wpPosts = getWpHealthFitness().length > 0 ?  getWpHealthFitness() : await getWpPostsByCategory(6, "health-fitness");
    setWpHealthFitness(wpPosts)
    const wpNewPosts = getWpNewHealthFitness().length > 0 ?  getWpNewHealthFitness() : await getWpNewPostsByCategory(6, "health-fitness");
    setWpNewHealthFitness(wpNewPosts)

    const allRequestCategorySettings = await getRequestJSON("Singapore")
    const testimonials = getPtTestimonials().length > 0 ? getPtTestimonials() : await getTestimonials("Fitness Trainer", 0, 6)
    setPtTestimonials(testimonials)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")

    const distanceUnit = getDistanceMetrics("Singapore")
    const lat = 1.3521
    const lng = 103.8198
    // This page promises freelance trainers, so it has to list freelance
    // trainers - the unfiltered top roster is the same generic list every other
    // page shows. There is no server-side freelance filter, so fetch the ranked
    // roster and filter on `isOnboarded` (the flag behind the public "Freelance
    // Trainer" badge) here.
    //
    // Do NOT switch this to downloadServiceProviders with isForContent=false:
    // that path returns only onboarded (employed) trainers whatever employeeOnly
    // says, so the freelance filter emptied the list and the "Top Freelance
    // Personal Trainers" section rendered blank. The top-providers endpoint
    // returns the whole roster with isOnboarded on every record.
    const rosterRaw = await downloadTopServiceProviders(
        "Fitness Trainer", 0, ROSTER_FETCH_SIZE, "Singapore", lat, lng, 50, distanceUnit,
        false,  // isOrderDistance
        ""      // gender - any
    )
    // Pass-through in a production build - see utils/dev-roster-fallback.
    const spList = withDevRosterFallback(rosterRaw, 'freelance trainers').filter(isFreelanceTrainer)

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        fitnessBlogs,
        wpPosts,
        wpNewPosts,
        testimonials,
        spList: toTrainerCards(spList).slice(0, ROSTER_DISPLAY_LIMIT)
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const fitnessBlogs = props.fitnessBlogs
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts
    const requestCategorySettings = props.requestCategorySettings;
    const testimonials = props.testimonials
    const spList = props.spList
    // The hero strip shows the first HERO_TRAINER_COUNT trainers, so the grid
    // below starts after them (as on /sg/personal-trainer). A roster too short
    // to split keeps the whole list in the grid rather than leave it empty.
    const gridList = spList.length > HERO_TRAINER_COUNT ? spList.slice(HERO_TRAINER_COUNT) : spList
    const router = useRouter()

    const [ requestForm, setRequestForm ] = useState(false)

    const requestType = "Fitness Trainer"
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

    useEffect(() => {
        if(router.query.requestForm === "true"){
            setRequestForm(true)
            openModal(setRequestForm)
        }
    }, [router])

  return (
    <>
      <Head>
        <title>{`Best Freelance Personal Trainer in Singapore [${year}]`}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Singapore residents look for freelance personal trainers who can train them at their home or condo gym, or they look for nearby gyms with personal training."/>
        <meta name="keywords" content="House Rental, Insurance Plan, Personal Trainer, Maid Service, Handyman Service, Aircon Service, Savings Plan, Food Catering, Interior Design, Wedding Planner, Event Planner"/>
        <meta name="author" content="StringsSG - Personal Training"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Best Freelance Personal Trainer in Singapore ${year}`}/>
        <meta property="og:description" content="Singapore residents look for freelance personal trainers who can train them at their home or condo gym, or they look for nearby gyms with personal training." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2026-01-06T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore"/>
        <meta name="twitter:title" content={`Best Female Personal Trainer in Singapore ${year}`}/> 
        <meta name="twitter:description" content="Singapore residents look for freelance personal trainers who can train them at their home or condo gym, or they look for nearby gyms with personal training."/>
        <link rel="icon" href="/favicon.ico" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@type":"BreadcrumbList","@id":"https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore/#breadcrumblist","itemListElement":[{"@type":"ListItem","@id":"https://www.stringssg.com/#listItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"},"nextItem":"https://www.stringssg.com/sg/personal-trainer/#listItem"},{"@type":"ListItem","@id":"https://www.stringssg.com/sg/personal-trainer/#listItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/","name":"Personal Trainer Singapore","url":"https://www.stringssg.com/sg/personal-trainer/"},"previousItem":"https://www.stringssg.com/#listItem","nextItem":"https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore/#listItem"},{"@type":"ListItem","@id":"https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore/#listItem","position":3,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore/","name":"Freelance Personal Trainer Singapore","description":"Singapore residents look for freelance personal trainers who can train them at their home or condo gym, or they look for nearby gyms with personal training.","url":"https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore/"},"previousItem":"https://www.stringssg.com/sg/personal-trainer/#listItem"}]},${JSON.stringify(localBusinessNode)},${JSON.stringify(buildServiceNode({ pageUrl: "https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore", name: "Freelance Personal Trainer Singapore", serviceType: "Personal Training", priceRange: "S$84.00 - S$110.00", image: "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg" }))},{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG Singapore","url":"https://www.stringssg.com/"},{"@type":"WebSite","@id":"https://www.stringssg.com/#website","url":"https://www.stringssg.com/","name":"StringsSG Singapore","inLanguage":"en-SG","publisher":{"@id":"https://www.stringssg.com/#organization"}},{"@type":"VideoObject","@id":"https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore/#video","name":"Freelance Personal Trainer Singapore - StringsSG","description":"Watch the different type of personal training offered by StringsSG - group classes, muscle building, kick boxing and more.","thumbnailUrl":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/fitness-package5.jpg","https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/fitness-package6.jpg"],"uploadDate":"2022-10-03T08:00:00+08:00","duration":"PT0M16S","contentUrl":"https://youtu.be/CC4Pe6-I1PY","embedUrl":"https://www.youtube.com/embed/CC4Pe6-I1PY","interactionStatistic":{"@type":"InteractionCounter","interactionType":{"@type":"WatchAction"},"userInteractionCount":5647018}},{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore/#webpage","url":"https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore/","name":"Best Freelance Personal Trainer in Singapore ${year}","description":"Singapore residents look for freelance personal trainers who can train them at their home or condo gym, or they look for nearby gyms with personal training.","inLanguage":"en-SG","isPartOf":{"@id":"https://www.stringssg.com/#website"},"breadcrumb":{"@id":"https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore/#breadcrumblist"},"video":{"@id":"https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore/#video"},"datePublished":"2025-01-06T07:30:46+00:00","dateModified":"${modifiedDate}"}]}`
          }}
        />
        {/* Declares the page as the trainer directory it actually is.
            Skipped when the roster is empty - an ItemList with zero items is
            worse than no ItemList at all. */}
        {spList.length > 0 &&
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildTrainerItemList({
            pageUrl: "https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore",
            name: "Freelance Personal Trainers in Singapore",
            trainers: spList
          })) }}
        />
        }
        <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer/freelance-personal-trainer-singapore" />
        </Head>
        <div>
            <RequestProvider>
                <PersonalTrainerHead 
                    heroImage={s3Static + "personal-trainer/personal-trainer.jpg"}
                    title={`Freelance Personal Trainer Singapore (${year})`}
                    heading={`Freelance Personal Trainer Singapore (${year})`}
                    heroCopy="Certified independent trainers from $84/session, with rates published upfront and no agency mark-up surprises."
                    setIsQuickBook={setIsQuickBook}
                    openRequestForm={openRequestForm}
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    isCustomBook={isCustomBook}
                    requestType={requestType}
                    requestCategorySettings={requestCategorySettings}
                    spList={spList}
                    seeAllHref="#top-trainers-singapore"
                    seeAllLabel="See all top freelance trainers"
                />
            </RequestProvider>
            <section className="mx-auto py-6 px-2 bg-slate-900 relative">
                {/* Mesh gradient */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/15 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl" />
                {/* <h2 className='text-2xl md:text-4xl text-center text-white font-extrabold tracking-tight py-5'>
                    Freelance Personal Trainer Singapore - 2026
                </h2> */}
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
                    <div className="space-y-5 text-white/60 text-lg leading-relaxed py-10">
                        <p>
                            A freelance personal trainer or a mobile trainer is not tied to a gym, which means they can train you at any place be it your home, condo gym or any community gym.                             they can serve you at any place be it your home, condo gym or any community gym. 
                        </p>  
                        <p>
                            They may also carry basic equipment with them like workout straps and grips to help you with your workout. 
                        </p>
                        <p className="mb-8 leading-relaxed">
                            At StringsSG, mobile personal training is one of the key services we offer through our dedicated team of full-time trainers and experienced 
                            fitness professionals across Singapore. We help you get started based on what works best for you:
                        </p>
                        <ul className="space-y-4">
                            <li className="font-bold text-white/90 flex items-center gap-2">
                                <FaCheckCircle className="text-orange-400" />
                                Your location
                            </li>
                            <li className="font-bold text-white/90 flex items-center gap-2">
                                <FaCheckCircle className="text-orange-400" />
                                Your fitness goals
                            </li>
                            <li className="font-bold text-white/90 flex items-center gap-2">
                                <FaCheckCircle className="text-orange-400" />
                                Your preferred training schedule
                            </li>
                        </ul>
                    </div>
                    {/* Image Section */}
                    <div className="relative h-62.5 lg:h-87.5">
                        {/* Floating badge - Certified */}
                        <div className="absolute bottom-4 right-4 z-10 bg-white/10 backdrop-blur-md rounded-2xl px-4 py-3 shadow-xl border border-white/20">
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 bg-linear-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                                    <FaCheckCircle className="text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-white/50">Certified</p>
                                    <p className="font-bold text-white/90">Trainers</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl">
                            <Image
                                src={s3Static + "personal-trainer/personal-trainer-png.png"}
                                fill
                                style={{ objectFit: 'contain', objectPosition: 'top' }}
                                alt="Personal Trainer Singapore - StringsSG"
                                sizes="(max-width: 768px) 100vw, 50vw"
                            />
                        </div>
                    </div>
                </div>
            </section> 
            {/* <TrustedPartners/>  */}
            <section id="top-trainers-singapore" className='relative bg-slate-900 overflow-hidden'>
                {/* Mesh gradient blobs */}
                <div className="absolute top-0 left-1/3 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-400/8 rounded-full blur-3xl" />
                
                <div className='relative py-10 max-w-7xl mx-auto px-3'>
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                            <span className="text-sm">🏆</span>
                            <span className="text-xs font-medium text-white/70">Top Trainers</span>
                        </div>
                        <h2 className='text-2xl md:text-4xl font-extrabold text-white tracking-tight'>
                            Top Freelance Personal Trainers In Singapore
                        </h2>
                    </div>
                    {/* No grid here: TopTrainersSection owns its own responsive grid. Nesting it
                        inside another one put the whole list in a single cell, so on desktop
                        it rendered a third of the width. */}
                    <div>
                        <TopTrainersSection spList={gridList}/>     
                    </div>                    
                    <p className='text-center text-white/60 leading-relaxed max-w-3xl mx-auto pt-6'>
                        Freelance trainers are usually the cheaper route because they carry no gym overheads. Our guide to{' '}
                        <Link href="/sg/personal-trainer/personal-trainer-rates-singapore" prefetch={false} className="text-orange-400/90 hover:text-orange-400 underline">
                            how much a personal trainer costs in Singapore
                        </Link>{' '}
                        sets freelance rates against commercial gym and studio pricing, with the membership fees included.
                    </p>
                    <div className='text-center pt-5'>
                        <Link href="/sg/personal-trainer/trainer-list/Singapore"
                            prefetch={false} className='px-8 py-4 text-lg text-white bg-linear-to-tr from-orange-600 to to-amber-400 rounded-full'>
                                View all Trainers and Gym
                        </Link>
                    </div>
                </div>          
            </section> 
            <FreelanceTrainerBenefits/>
            <FreelanceTrainerComparison/>
            <PersonalTrainerWhyUs/>
            <TrainerCustomerReviews testimonials={testimonials}/>
            <section className="relative py-8 md:py-12 overflow-hidden bg-slate-900">
                <div className="absolute top-0 left-0 w-72 h-72 bg-orange-500/8 rounded-full blur-3xl" />
                <div className='relative max-w-5xl mx-auto px-4'>
                    <DarkFaq 
                        title="Personal Training FAQs"
                        questionsAnswers={ptFreelanceFaqs} 
                    />
                </div>
            </section>
            <BlogsList blogs={fitnessBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts} theme="dark"/>
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
